import React from 'react';
import { colors, styles, typography } from '../theme';
import LivePreviewCard from './LivePreviewCard';
import { useCharacterDraft } from '../hooks/useCharacterDraft';

// Two-pane customize layout (Tier 2 path).
// Left: sectioned, scrollable form. Right: persistent LivePreviewCard.
//
// Phase 2 keeps the left pane minimal — only Identity (name) is interactive,
// all other sections are collapsed reads of the archetype's picks. Phase 2.5
// (engine + interactive editing) unlocks ability score allocation, skill
// re-picking, equipment alternates, etc. The collapsed sections still SHOW
// what the archetype chose for the user (P1+P2 tension resolved per the plan).

const Section = ({ title, badge, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <section
      data-testid={testId}
      style={{
        background: colors.bgCardSolid,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 6,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: '14px 16px',
          textAlign: 'left',
          cursor: 'pointer',
          color: colors.textPrimary,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: typography.fontFamily,
          fontSize: 15,
        }}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ color: colors.textMuted, fontSize: 12 }}>{open ? '▼' : '▶'}</span>
          {title}
          {badge ? (
            <span
              style={{
                fontSize: 11,
                color: colors.textMuted,
                fontStyle: 'italic',
                marginLeft: 8,
              }}
            >
              {badge}
            </span>
          ) : null}
        </span>
      </button>
      {open ? (
        <div style={{ padding: '0 16px 16px', color: colors.textSecondary, fontSize: 13, lineHeight: 1.5 }}>
          {children}
        </div>
      ) : null}
    </section>
  );
};

const ReadOnlyField = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>
      {label}
    </div>
    <div style={{ color: colors.textPrimary, fontSize: 13 }}>{value || '—'}</div>
  </div>
);

const CustomizeScreen = ({ archetype, onBack, onComplete, onAdvanced }) => {
  // Phase 2.5: the engine derives stats live from the archetype + edits.
  // Phase 2 (pre-engine) read static `archetype.previewStats`. The hook
  // returns `preview = null` when there's no archetype.
  const { preview, name, setName } = useCharacterDraft(archetype);

  if (!archetype) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <p style={{ color: colors.textMuted, fontStyle: 'italic' }}>
            No archetype selected.{' '}
            <button type="button" onClick={onBack} style={styles.buttonGhost}>
              ← Go back
            </button>
          </p>
        </div>
      </div>
    );
  }

  const picks = archetype.picks || {};

  return (
    <div style={styles.page}>
      <div style={{ ...styles.container, maxWidth: 1200 }}>
        <button
          type="button"
          onClick={onBack}
          data-testid="back-to-gallery"
          style={{ ...styles.buttonGhost, marginBottom: 16 }}
        >
          ← Back to archetypes
        </button>

        <header style={{ marginBottom: 24 }}>
          <h1
            style={{
              ...styles.heading,
              fontSize: 32,
              marginBottom: 4,
            }}
          >
            Customizing: {archetype.name}
          </h1>
          <p style={styles.subheading}>{archetype.tagline}</p>
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 360px',
            gap: 24,
            alignItems: 'start',
          }}
          data-testid="customize-layout"
        >
          {/* Left: editable sections */}
          <div data-testid="customize-form">
            <Section title="Identity" defaultOpen testId="section-identity">
              <label
                htmlFor="character-name-input"
                style={{ display: 'block', fontSize: 12, color: colors.textMuted, marginBottom: 4 }}
              >
                Name
              </label>
              <input
                id="character-name-input"
                data-testid="character-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name…"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: colors.bgInput,
                  border: `1px solid ${colors.borderLight}`,
                  borderRadius: 4,
                  color: colors.textPrimary,
                  fontFamily: typography.fontFamily,
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 6, fontStyle: 'italic' }}>
                Portrait selection and random name generation arrive in a later phase.
              </div>
            </Section>

            <Section title="Ability Scores" badge="(auto, click to customize)" testId="section-abilities">
              <ReadOnlyField label="Strength" value={picks.abilities?.str} />
              <ReadOnlyField label="Dexterity" value={picks.abilities?.dex} />
              <ReadOnlyField label="Constitution" value={picks.abilities?.con} />
              <ReadOnlyField label="Intelligence" value={picks.abilities?.int} />
              <ReadOnlyField label="Wisdom" value={picks.abilities?.wis} />
              <ReadOnlyField label="Charisma" value={picks.abilities?.cha} />
              <p style={{ fontStyle: 'italic', color: colors.textMuted, fontSize: 12 }}>
                Pre-allocated for this archetype. Editing arrives in a later phase.
              </p>
            </Section>

            <Section title="Class & Species" badge="(from archetype)" testId="section-class-species">
              <ReadOnlyField label="Class" value={picks.class} />
              {picks.subclass ? <ReadOnlyField label="Subclass" value={picks.subclass} /> : null}
              <ReadOnlyField label="Species" value={picks.species} />
              <ReadOnlyField label="Background" value={picks.background} />
            </Section>

            <Section title="Skills" badge="(from archetype)" testId="section-skills">
              {(picks.skills || []).map((skill) => (
                <div
                  key={skill}
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    margin: '2px 4px 2px 0',
                    background: colors.bgInput,
                    border: `1px solid ${colors.borderLight}`,
                    borderRadius: 4,
                    fontSize: 12,
                    color: colors.textPrimary,
                  }}
                >
                  {skill}
                </div>
              ))}
            </Section>

            <Section title="Starting Equipment" badge="(from archetype)" testId="section-equipment">
              <div>{picks.startingEquipment || '—'}</div>
              <p style={{ fontStyle: 'italic', color: colors.textMuted, fontSize: 12, marginTop: 8 }}>
                Equipment resolution + alternates arrive in a later phase.
              </p>
            </Section>

            <Section title="Backstory" badge="(optional)" testId="section-backstory">
              <p style={{ fontStyle: 'italic', color: colors.textMuted, fontSize: 12 }}>
                Free-text backstory with NPC/quest autocomplete arrives in a later phase.
              </p>
            </Section>

            <div
              style={{
                display: 'flex',
                gap: 12,
                marginTop: 24,
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={() => onComplete?.({ name, archetype })}
                data-testid="start-playing"
                style={{ ...styles.buttonPrimary, flex: '1 1 200px' }}
              >
                Start Playing
              </button>
              <button
                type="button"
                onClick={onAdvanced}
                data-testid="open-advanced"
                style={{ ...styles.buttonSecondary, flex: '1 1 160px' }}
              >
                Advanced →
              </button>
            </div>
          </div>

          {/* Right: persistent preview */}
          <LivePreviewCard archetype={archetype} customName={name} preview={preview} />
        </div>
      </div>
    </div>
  );
};

export default CustomizeScreen;
